import { NOTE_COLOR_LABELS, TRASH_RETENTION_DAYS, type NoteDto } from '@keep/shared';
import {
  Archive,
  Bell,
  Image as ImageIcon,
  Lightbulb,
  Palette,
  SearchX,
  SquareCheckBig,
  Tag,
  Trash2,
  type LucideIcon,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router';
import { usePreferences } from '../../api/auth';
import { useLabels } from '../../api/labels';
import { useEmptyTrash, useMoveNote, useNotes, type NoteFilter } from '../../api/notes';
import { ConfirmDialog } from '../../components/ui/dialog';
import { Spinner } from '../../components/ui/spinner';
import { useIsMobile } from '../../hooks/use-media-query';
import { errorMessage } from '../../lib/api';
import { NOTE_COLOR_OPTIONS, noteColorVar } from '../../lib/colors';
import { reminderPresets } from '../../lib/dates';
import { filterFromLocation, paths } from '../../lib/routes';
import { toast, toastError } from '../../stores/toasts';
import { useUi } from '../../stores/ui';
import { CollaboratorsDialog } from './collaborators-dialog';
import { MasonryGrid } from './masonry-grid';
import { NoteCard } from './note-card';
import { NoteComposer } from './note-composer';
import { useNoteActions } from './use-note-actions';

function EmptyState({
  icon: Icon,
  children,
  extra,
}: {
  icon: LucideIcon;
  children: ReactNode;
  extra?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-4 pt-[12vh] text-center text-muted">
      <Icon size={120} strokeWidth={1} className="opacity-20" aria-hidden />
      <p className="max-w-sm text-[22px] leading-7">{children}</p>
      {extra}
    </div>
  );
}

/** Suggestion d'import quand le compte est encore vide. */
function ImportHint() {
  const openImport = useUi((s) => s.setImportOpen);
  return (
    <button
      type="button"
      onClick={() => openImport(true)}
      className="text-sm text-primary hover:underline"
    >
      Vous venez de Google Keep ? Importez vos notes
    </button>
  );
}

function SectionTitle({ children }: { children: ReactNode }) {
  return (
    <h2 className="mx-auto mt-6 mb-2 w-full max-w-[1200px] px-2 text-[11px] font-medium tracking-[0.08em] text-muted uppercase">
      {children}
    </h2>
  );
}

/** Écran de recherche sans critère : raccourcis par type, libellé et couleur, comme dans Keep. */
function SearchFilters() {
  const { data: labels = [] } = useLabels();
  const tile =
    'flex h-28 w-28 flex-col items-center justify-end gap-3 rounded-sm pb-3 text-[13px] text-white transition-opacity hover:opacity-90';
  return (
    <div className="mx-auto flex max-w-[740px] flex-col gap-6 pt-8">
      <section className="rounded-lg border border-line p-4">
        <h2 className="mb-3 text-sm font-medium">Types</h2>
        <div className="flex flex-wrap gap-2">
          <Link to={`${paths.search}?type=checklist`} className={`${tile} bg-[#4285f4]`}>
            <SquareCheckBig size={40} /> Listes
          </Link>
          <Link to={`${paths.search}?images=true`} className={`${tile} bg-[#fbbc04]`}>
            <ImageIcon size={40} /> Images
          </Link>
        </div>
      </section>
      {labels.length > 0 && (
        <section className="rounded-lg border border-line p-4">
          <h2 className="mb-3 text-sm font-medium">Libellés</h2>
          <div className="flex flex-wrap gap-2">
            {labels.map((label) => (
              <Link key={label.id} to={paths.label(label.id)} className={`${tile} bg-[#80868b]`}>
                <Tag size={40} />
                <span className="w-full truncate px-2 text-center">{label.name}</span>
              </Link>
            ))}
          </div>
        </section>
      )}
      <section className="rounded-lg border border-line p-4">
        <h2 className="mb-3 text-sm font-medium">Couleurs</h2>
        <div className="flex flex-wrap gap-2">
          {NOTE_COLOR_OPTIONS.filter((c) => c.id !== 'default').map((color) => (
            <Link
              key={color.id}
              to={`${paths.search}?color=${color.id}`}
              title={color.label}
              aria-label={`Notes ${color.label}`}
              style={{ backgroundColor: noteColorVar(color.id) }}
              className="size-12 rounded-full border border-line hover:border-fg"
            />
          ))}
        </div>
      </section>
    </div>
  );
}

function emptyMessage(filter: NoteFilter, isSearch: boolean): { icon: LucideIcon; text: string } {
  if (isSearch) return { icon: SearchX, text: 'Aucun résultat correspondant' };
  if (filter.labelId) return { icon: Tag, text: 'Aucune note avec ce libellé pour l’instant' };
  switch (filter.view) {
    case 'reminders':
      return { icon: Bell, text: 'Les notes avec un rappel à venir s’affichent ici' };
    case 'archive':
      return { icon: Archive, text: 'Vos notes archivées s’affichent ici' };
    case 'trash':
      return { icon: Trash2, text: 'Aucune note dans la corbeille' };
    default:
      return { icon: Lightbulb, text: 'Les notes que vous ajoutez s’affichent ici' };
  }
}

function searchDescription(filter: NoteFilter): string | null {
  const parts: string[] = [];
  if (filter.type === 'checklist') parts.push('Listes');
  if (filter.hasImages) parts.push('Images');
  if (filter.color) parts.push(`Couleur : ${NOTE_COLOR_LABELS[filter.color]}`);
  return parts.length ? parts.join(' · ') : null;
}

export function NotesPage() {
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  // On ne dépend que des critères de filtre (pas de ?note=…) : ouvrir une note ne recharge pas la liste.
  const q = params.get('q');
  const color = params.get('color');
  const type = params.get('type');
  const images = params.get('images');
  const filter = useMemo(
    () =>
      filterFromLocation(
        location.pathname,
        new URLSearchParams({
          ...(q && { q }),
          ...(color && { color }),
          ...(type && { type }),
          ...(images && { images }),
        }),
      ),
    [location.pathname, q, color, type, images],
  );
  const isSearch = location.pathname.startsWith(paths.search);
  const hasCriteria = Boolean(filter.q || filter.color || filter.type || filter.hasImages);
  const { data: notes, isPending, error } = useNotes(filter);
  const { layout } = usePreferences();
  const isMobile = useIsMobile();
  const actions = useNoteActions();
  const move = useMoveNote();
  const emptyTrash = useEmptyTrash();
  const selection = useUi((s) => s.selection);
  const toggleSelected = useUi((s) => s.toggleSelected);
  const clearSelection = useUi((s) => s.clearSelection);
  const [collaboratorsFor, setCollaboratorsFor] = useState<NoteDto | null>(null);
  const [confirmEmpty, setConfirmEmpty] = useState(false);
  const openNoteId = params.get('note');

  // Changer de vue vide la sélection.
  useEffect(() => clearSelection(), [filter, clearSelection]);

  const openNote = useCallback(
    (note: NoteDto) =>
      setParams((current) => {
        const next = new URLSearchParams(current);
        next.set('note', note.id);
        return next;
      }),
    [setParams],
  );
  const toggleSelect = useCallback((note: NoteDto) => toggleSelected(note.id), [toggleSelected]);
  const onMove = useCallback(
    (id: string, beforeId: string | null, afterId: string | null) =>
      move.mutate({ id, beforeId, afterId }, { onError: (e) => toastError(errorMessage(e)) }),
    [move],
  );

  const mode = isMobile ? 'grid' : layout;
  const sortable = !isSearch && filter.view !== 'trash' && filter.view !== 'reminders';
  const selectionMode = selection.size > 0;

  const renderCard = useCallback(
    (note: NoteDto, overlay = false) => (
      <div className={note.id === openNoteId && !overlay ? 'invisible' : undefined}>
        <NoteCard
          note={note}
          actions={actions}
          selected={selection.has(note.id)}
          selectionMode={selectionMode}
          onOpen={openNote}
          onToggleSelect={toggleSelect}
          onCollaborators={setCollaboratorsFor}
          overlay={overlay}
        />
      </div>
    ),
    [actions, selection, selectionMode, openNote, toggleSelect, openNoteId],
  );

  const composerDefaults = useMemo(() => {
    if (filter.labelId) return { labelIds: [filter.labelId] };
    if (filter.view === 'reminders')
      return { reminderAt: reminderPresets()[0]!.date.toISOString() };
    return undefined;
  }, [filter]);
  const showComposer =
    !isSearch &&
    (filter.view === 'notes' || filter.view === 'reminders' || Boolean(filter.labelId));

  const grid = (items: NoteDto[]) => (
    <MasonryGrid
      items={items}
      mode={mode}
      sortable={sortable}
      onMove={onMove}
      renderItem={(note) => renderCard(note)}
      renderOverlay={(note) => renderCard(note, true)}
    />
  );

  let body: ReactNode;
  if (isSearch && !hasCriteria) {
    body = <SearchFilters />;
  } else if (isPending) {
    body = (
      <div className="flex justify-center pt-16">
        <Spinner className="size-8" />
      </div>
    );
  } else if (error) {
    body = <p className="pt-16 text-center text-muted">{errorMessage(error)}</p>;
  } else if (!notes?.length) {
    const { icon, text } = emptyMessage(filter, isSearch);
    body = (
      <EmptyState
        icon={icon}
        extra={filter.view === 'notes' && !filter.labelId ? <ImportHint /> : undefined}
      >
        {text}
      </EmptyState>
    );
  } else if (filter.view === 'reminders') {
    const now = Date.now();
    const upcoming = notes.filter((n) => new Date(n.reminderAt!).getTime() >= now);
    const past = notes.filter((n) => new Date(n.reminderAt!).getTime() < now);
    body = (
      <>
        {upcoming.length > 0 && past.length > 0 && <SectionTitle>À venir</SectionTitle>}
        {upcoming.length > 0 && grid(upcoming)}
        {past.length > 0 && <SectionTitle>Passés</SectionTitle>}
        {past.length > 0 && grid(past)}
      </>
    );
  } else {
    const pinned = filter.view === 'trash' ? [] : notes.filter((n) => n.pinned);
    const others = filter.view === 'trash' ? notes : notes.filter((n) => !n.pinned);
    body = (
      <>
        {pinned.length > 0 && <SectionTitle>Épinglées</SectionTitle>}
        {pinned.length > 0 && grid(pinned)}
        {pinned.length > 0 && others.length > 0 && <SectionTitle>Autres</SectionTitle>}
        {others.length > 0 && (
          <div className={pinned.length ? undefined : 'mt-6'}>{grid(others)}</div>
        )}
      </>
    );
  }

  const description = isSearch ? searchDescription(filter) : null;

  return (
    <div className="mx-auto w-full max-w-[1600px]">
      {showComposer && (
        <NoteComposer key={`${filter.view}-${filter.labelId ?? ''}`} defaults={composerDefaults} />
      )}
      {filter.view === 'trash' && (
        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 pt-6 text-[17px] text-fg italic">
          <span>
            Les notes de la corbeille sont supprimées au bout de {TRASH_RETENTION_DAYS} jours.
          </span>
          {!!notes?.length && (
            <button
              type="button"
              onClick={() => setConfirmEmpty(true)}
              className="rounded px-4 py-2 text-sm font-medium text-primary not-italic hover:bg-hover"
            >
              Vider la corbeille
            </button>
          )}
        </div>
      )}
      {description && (
        <div className="flex items-center justify-center gap-2 pt-6 text-sm text-muted">
          {filter.type === 'checklist' ? (
            <SquareCheckBig size={16} />
          ) : filter.hasImages ? (
            <ImageIcon size={16} />
          ) : (
            <Palette size={16} />
          )}
          {description}
          <Link to={paths.search} className="ml-2 text-primary hover:underline">
            Effacer
          </Link>
        </div>
      )}
      {body}
      {collaboratorsFor && (
        <CollaboratorsDialog note={collaboratorsFor} onClose={() => setCollaboratorsFor(null)} />
      )}
      <ConfirmDialog
        open={confirmEmpty}
        onOpenChange={setConfirmEmpty}
        title="Vider la corbeille ?"
        message="Toutes les notes de la corbeille seront supprimées définitivement."
        confirmLabel="Vider la corbeille"
        onConfirm={() =>
          emptyTrash.mutate(undefined, {
            onSuccess: ({ deleted }) =>
              toast(`${deleted} ${deleted > 1 ? 'notes supprimées' : 'note supprimée'}`),
            onError: (e) => toastError(errorMessage(e)),
          })
        }
      />
    </div>
  );
}
